/**
 * "Meus Desktops" — fixed top bar for quick desktop switching. SUPERFÍCIE WEB.
 *
 * A lógica (lista, favoritos, dirty, troca, reload) vive em `useMyDesktops` —
 * fonte única compartilhada com o menu nativo "Favoritos" do app desktop
 * (`~/desktop/NativeFavoritesMenu`). Esta barra é só o desenho dela no browser.
 *
 * Content rule: with ≥1 favorite shows only favorites, otherwise the first 5
 * desktops (same order as the Load Desktop dialog / server list). Clicking a
 * section loads it directly — no confirmation: the save-on-switch local cache
 * (workspace/desktopManager) makes switching lossless.
 *
 * Gated by: feature flag `myDesktops` + a "Mostrar barra Meus Desktops" user
 * setting + NÃO estar no app desktop (lá a superfície é o menu nativo).
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { ContextMenu } from "primereact/contextmenu";
import { confirmDialog } from "primereact/confirmdialog";
import { Checkbox } from "primereact/checkbox";
import { Tooltip } from "primereact/tooltip";
import { useSettingsStore } from "~/store/settingsStore";
import { isFeatureEnabled } from "~/data/Services/settings";
import { eventBus, EVENTS } from "~/utils/eventBus";
import { isDesktopApp } from "~/desktop/platform";
import { isMobilePhone } from "~/utils/mobileDetection";
import { useMyDesktops } from "./useMyDesktops";
import "./MyDesktopsBar.css";

/**
 * Bar visibility = feature flag (render-time read) AND user setting AND web.
 * Shared with Main, which must swap the workspace top offset when the bar
 * occupies the strip below the fixed navbar.
 *
 * No app desktop a barra NÃO aparece: a mesma feature é servida pelo menu
 * nativo "Favoritos". Feature igual, superfície diferente.
 */
export const useMyDesktopsBarVisible = () => {
  const showMyDesktopsBar = useSettingsStore(
    (state) => state.showMyDesktopsBar ?? true,
  );
  // Phones: the bar costs a full row of scarce vertical space — hidden.
  return (
    !isMobilePhone() &&
    isFeatureEnabled("myDesktops") &&
    showMyDesktopsBar &&
    !isDesktopApp()
  );
};

const MyDesktopsBar = () => {
  const visible = useMyDesktopsBarVisible();

  const {
    sections,
    hasFavorites,
    loaded,
    currentDesktopId,
    isDirty,
    switchTo,
    confirmReload,
    favoriteIds,
    toggleFavorite,
    currentDesktop,
  } = useMyDesktops(visible);

  const setShowMyDesktopsBar = useSettingsStore(
    (state) => state.setShowMyDesktopsBar,
  );

  // Empty-state: user has no favorite yet. Gate on `loaded` so it never flashes
  // before the first desktop fetch resolves (the whole reason the hook exposes it).
  const showEmpty = loaded && !hasFavorites;

  const [overflow, setOverflow] = useState({ left: false, right: false });
  const scrollerRef = useRef(null);
  const contextMenuRef = useRef(null);
  const contextTargetRef = useRef(null);

  const isFavorite = useCallback(
    (id) => favoriteIds?.includes(id),
    [favoriteIds],
  );

  const handleStarClick = useCallback(
    (event, desktop) => {
      event.stopPropagation(); // não trocar de desktop ao clicar na estrela
      const id = desktop.id;
      if (isFavorite(id)) {
        // Desfavoritar remove o desktop da barra → confirma antes (evita perder
        // um favorito por clique errado). Só aqui: fixar não pede confirmação.
        confirmDialog({
          message: `Remover "${desktop.name}" dos Meus Desktops?`,
          header: "Remover dos favoritos",
          icon: "pi pi-star",
          acceptLabel: "Remover",
          rejectLabel: "Cancelar",
          accept: () => toggleFavorite(id),
        });
        return;
      }
      // Estrela vazia (fantasma / nunca-favorito) → favorita direto, sem diálogo.
      toggleFavorite(id);
    },
    [isFavorite, toggleFavorite],
  );

  // Slot único do "carregado não-favorito" (limite 1, memória de sessão).
  // Lembra o ÚLTIMO desktop não-favorito aberto — persiste mesmo depois de
  // trocar para um favorito (some o problema de "sumir ao clicar num favorito").
  // Trocado quando você abre outro não-favorito; some quando vira favorito.
  const [ghostDesktop, setGhostDesktop] = useState(null);
  useEffect(() => {
    if (currentDesktop && !isFavorite(currentDesktop.id)) {
      setGhostDesktop(currentDesktop);
    }
  }, [currentDesktop, isFavorite]);

  const ghost =
    ghostDesktop &&
    !isFavorite(ghostDesktop.id) &&
    !sections.some((s) => s.id === ghostDesktop.id)
      ? ghostDesktop
      : null;

  // Overflow detection for the < > scroll buttons
  const updateOverflow = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const left = el.scrollLeft > 0;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setOverflow((prev) =>
      prev.left === left && prev.right === right ? prev : { left, right },
    );
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    updateOverflow();
    const el = scrollerRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(updateOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, sections.length, updateOverflow]);

  const scrollBy = (direction) => {
    scrollerRef.current?.scrollBy({
      left: direction * 240,
      behavior: "smooth",
    });
  };

  const handleContextMenu = (event, desktop) => {
    event.preventDefault();
    if (!isDirty(desktop)) return; // menu only exists for dirty desktops
    contextTargetRef.current = desktop;
    contextMenuRef.current?.show(event);
  };

  const contextMenuModel = [
    {
      label: "Recarregar do servidor",
      icon: "pi pi-refresh",
      command: () => {
        if (contextTargetRef.current) confirmReload(contextTargetRef.current);
      },
    },
  ];

  const renderPill = (desktop, { isGhost = false } = {}) => {
    const dirty = isDirty(desktop);
    const starFilled = isFavorite(desktop.id);
    return (
      <div
        key={desktop.id}
        className={`my-desktops-section${
          desktop.id === currentDesktopId ? " active" : ""
        }${isGhost ? " ghost" : ""}`}
        onClick={() => switchTo(desktop)}
        onContextMenu={(e) => handleContextMenu(e, desktop)}
        data-pr-tooltip={dirty ? "Modificações não salvas" : desktop.name}
        data-evo-id={`my-desktops-section-${desktop.id}`}
      >
        <span className="my-desktops-section-name">{desktop.name}</span>
        <i
          className={`my-desktops-star desktop-favorite-star pi ${
            starFilled ? "pi-star-fill" : "pi-star"
          }`}
          style={{
            fontSize: "12px",
            cursor: "pointer",
            color: starFilled
              ? "var(--yellow-500, #f5c518)"
              : "var(--text-color-secondary)",
          }}
          onClick={(e) => handleStarClick(e, desktop)}
          title={starFilled ? "Remover dos favoritos" : "Fixar nos favoritos"}
          data-evo-id={`my-desktops-star-${desktop.id}`}
        />
        {dirty && <span className="my-desktops-dirty">*</span>}
        {dirty && (
          <button
            className="my-desktops-reload"
            onClick={(e) => {
              e.stopPropagation();
              confirmReload(desktop);
            }}
            title="Descartar mudanças locais e recarregar do servidor"
            data-evo-id={`my-desktops-reload-${desktop.id}`}
          >
            <i className="pi pi-replay" />
          </button>
        )}
      </div>
    );
  };

  if (!visible) return null;

  return (
    <div className="my-desktops-bar" data-evo-id="my-desktops-bar">
      <Tooltip
        target=".my-desktops-section"
        showDelay={400}
        position="bottom"
      />
      <ContextMenu ref={contextMenuRef} model={contextMenuModel} />

      {showEmpty ? (
        <div className="my-desktops-empty">
          <span
            className="my-desktops-empty-text"
            onClick={() => eventBus.emit(EVENTS.OPEN_LOAD_DESKTOP_DIALOG)}
            title="Todos os desktops…"
            data-evo-id="my-desktops-empty-open"
          >
            Abra daqui seus desktops favoritos. Marque com ⭐ em Carregar
            Desktops.
          </span>
          <button
            className="my-desktops-add"
            onClick={() => eventBus.emit(EVENTS.OPEN_LOAD_DESKTOP_DIALOG)}
            title="Todos os desktops…"
            data-evo-id="my-desktops-add"
          >
            <i className="pi pi-plus" />
          </button>
          <label
            className="my-desktops-empty-hide"
            data-evo-id="my-desktops-hide-bar"
          >
            <Checkbox
              checked={false}
              onChange={() => setShowMyDesktopsBar(false)}
            />
            <span>Esconder barra</span>
          </label>
        </div>
      ) : (
        <>
          {/* "Você está aqui": desktop carregado que não está na barra. Fixo
              à esquerda, FORA do scroller (sempre visível), com estrela para
              fixá-lo nos favoritos. Divisor separa dos favoritos. */}
          {ghost && (
            <>
              {renderPill(ghost, { isGhost: true })}
              <div className="my-desktops-ghost-divider" />
            </>
          )}

          {overflow.left && (
            <button
              className="my-desktops-scroll-btn"
              onClick={() => scrollBy(-1)}
              data-evo-id="my-desktops-scroll-left"
            >
              <i className="pi pi-chevron-left" />
            </button>
          )}

          <div
            className="my-desktops-sections"
            ref={scrollerRef}
            onScroll={updateOverflow}
          >
            {sections.map((desktop) => renderPill(desktop))}

            <button
              className="my-desktops-add"
              onClick={() => eventBus.emit(EVENTS.OPEN_LOAD_DESKTOP_DIALOG)}
              title="Todos os desktops…"
              data-evo-id="my-desktops-add"
            >
              <i className="pi pi-plus" />
            </button>
          </div>

          {overflow.right && (
            <button
              className="my-desktops-scroll-btn"
              onClick={() => scrollBy(1)}
              data-evo-id="my-desktops-scroll-right"
            >
              <i className="pi pi-chevron-right" />
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default MyDesktopsBar;
